<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import {
        useQueryGetBackOfficeTenants,
        useMutationDeleteTenant,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { TriangleAlertIcon } from '@lucide/vue';
    import { isProd } from '@/config';

    const { data: tenants } = useQueryGetBackOfficeTenants();
    const { mutateAsync: deleteTenantMutation, isPending } = useMutationDeleteTenant();
    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const confirmText = ref('');

    const tenantOptions = computed<Option[]>(() =>
        (tenants.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const selectedTenant = computed(
        () => (tenants.value ?? []).find((t) => t.id === selectedTenantId.value) ?? null
    );

    const warningParts = computed(() => {
        const parts = __('back-office-delete-tenant-warning').split('{name}');
        return { before: parts[0], after: parts[1] ?? '' };
    });

    const canDelete = computed(
        () => !!selectedTenant.value && confirmText.value.toLowerCase() === 'delete'
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
        confirmText.value = '';
    };

    const onTenantClear = () => {
        selectedTenantId.value = '';
        confirmText.value = '';
    };

    const submit = async () => {
        if (!canDelete.value || !selectedTenant.value) return;
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('back-office-delete-tenant-confirm-title'),
            message: __('back-office-delete-tenant-confirm-message').replace(
                '{name}',
                selectedTenant.value.name
            ),
            confirmText: __('back-office-delete-tenant-confirm-action'),
        });
        if (!confirmed) return;
        await deleteTenantMutation(selectedTenantId.value);
        tenantSearch.value = null;
        selectedTenantId.value = '';
        confirmText.value = '';
        successDialogStore.openDialog({ message: __('back-office-delete-tenant-success') });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-delete-tenant') }}</h1>
        </template>
        <template #content>
            <div v-if="isProd" class="flex items-center gap-3 text-sentiment-negative">
                <TriangleAlertIcon class="size-5 shrink-0" />
                <p class="text-body">{{ __('back-office-delete-tenant-dev-only') }}</p>
            </div>

            <form v-else class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="delete-tenant-select"
                        :label="__('back-office-delete-tenant-select')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />

                    <template v-if="selectedTenant">
                        <div
                            class="flex gap-3 rounded-lg border border-sentiment-negative bg-sentiment-negative/10 p-4 max-w-lg"
                        >
                            <TriangleAlertIcon
                                class="size-5 shrink-0 text-sentiment-negative mt-0.5"
                            />
                            <p class="text-body text-sentiment-negative">
                                {{ warningParts.before }}
                                <strong>{{ selectedTenant.name }}</strong>
                                {{ warningParts.after }}
                            </p>
                        </div>
                        <div class="mt-6">
                            <label for="delete-tenant-confirm" class="text-body-bold mb-1 block">
                                {{
                                    __('back-office-delete-tenant-confirm-label').replace(
                                        '{name}',
                                        selectedTenant.name
                                    )
                                }}
                            </label>
                            <TextInput
                                id="delete-tenant-confirm"
                                v-model="confirmText"
                                :width="TextInputWidth.Large"
                                placeholder="DELETE"
                                autocomplete="off"
                            />
                        </div>
                    </template>
                </FormGroup>

                <div class="mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canDelete"
                        :loading="isPending"
                    >
                        {{ __('back-office-delete-tenant-button') }}
                    </EchoButton>
                </div>
            </form>
        </template>
    </SettingsPageLayout>
</template>
